import { lazy, Suspense, useEffect, useState } from 'react';
import { Stack, useRouter, useSegments, usePathname } from 'expo-router';
import { View, StyleSheet, Platform, TextInput } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { configureReanimatedLogger, ReanimatedLogLevel } from 'react-native-reanimated';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import * as Sentry from '@sentry/react-native';
// Per-weight subpath imports: the package barrel re-exports all 18 Inter
// variants, and Metro can't tree-shake the CJS requires — the web export
// shipped ~4.7MB of unused TTFs in dist/assets. Subpaths emit only the
// four weights we load.
import { Inter_300Light } from '@expo-google-fonts/inter/300Light';
import { Inter_400Regular } from '@expo-google-fonts/inter/400Regular';
import { Inter_500Medium } from '@expo-google-fonts/inter/500Medium';
import { Inter_600SemiBold } from '@expo-google-fonts/inter/600SemiBold';
import { Inter_700Bold } from '@expo-google-fonts/inter/700Bold';
import { useFonts } from 'expo-font';
import Constants from 'expo-constants';
import { useAuthStore } from '../src/store/authStore';
import { useThemeStore } from '../src/store/themeStore';
import { useNotificationsStore } from '../src/store/notificationsStore';
import { useServiceAreasStore } from '../src/store/serviceAreasStore';
import { useTheme, durations } from '../src/theme';
import { LoadingSpinner, ConfirmProvider, ErrorBoundary, ToastHost } from '@components/common';
import { GlobalFloatingTabBar, AppShell } from '@components/navigation';
import { UserMenuDropdown } from '../src/components/user-menu/UserMenuDropdown';
import { AuthGateBottomSheet } from '../src/components/auth/AuthGateBottomSheet';
import { CreateActionSheet } from '../src/components/navigation/CreateActionSheet';
import { HowReDIWorksSheet, ObservationGuidanceSheet } from '../src/components/onboarding';
import { MapLayersSheet } from '../src/components/map/MapLayersSheet';
import { MapRailTools } from '../src/components/map/MapRailTools.web';
import { CommandPaletteHost } from '../src/components/command/CommandPalette';
import { AddPropertySheet } from '../src/components/structures/AddPropertySheet';
import { PropertyPickerSheet } from '../src/components/structures/PropertyPickerSheet';
import { isMapRoute } from '../src/utils/sidebarVariant';
import { useUIStore } from '../src/store/uiStore';
import { useSyncWebThemeVars } from '../src/hooks/useSyncWebThemeVars';
import { useBrandResolver } from '../src/hooks/useActiveBrand';
import { useDesktopShell } from '../src/hooks/useDesktopShell';
import { useOutboxSync } from '../src/hooks/useOutboxSync';
import { User } from '../src/types';
import { initSentry } from '@utils/sentry';
import { registerServiceWorker } from '@utils/serviceWorker';

// @gorhom/bottom-sheet v5 writes to shared values inside its own render (via
// useReactiveSharedValue) which trips Reanimated 4's strict-mode warning on
// every sheet open. The writes are library-internal and harmless; keep
// real-error logging but turn off the strict-mode lint chatter.
configureReanimatedLogger({ level: ReanimatedLogLevel.warn, strict: false });

initSentry();

// Polyfill TextInput.State methods missing on web — needed by @gorhom/bottom-sheet.
// The RN web build may omit `currentlyFocusedInput` even though the type declarations
// include it, so we patch at runtime via a record-style access.
if (Platform.OS === 'web' && TextInput.State) {
  const state = TextInput.State as unknown as Record<string, unknown>;
  if (typeof state.currentlyFocusedInput !== 'function') {
    state.currentlyFocusedInput = () => null;
  }
}

// Suppress harmless @gorhom/bottom-sheet warning on web. The library's
// findNodeHandle.web.ts warns when it can't locate a scrollable ref,
// but the sheet still functions correctly.
if (Platform.OS === 'web') {
  const originalWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    if (typeof args[0] === 'string' && args[0].includes('could not find scrollable ref')) return;
    originalWarn(...args);
  };
}

export const unstable_settings = {
  initialRouteName: '(tabs)',
};

function useProtectedRoute(user: User | null, isInitialized: boolean) {
  const segments = useSegments();
  const router = useRouter();

  useEffect(() => {
    if (!isInitialized) return;

    const inAuthGroup = segments[0] === '(auth)';
    const inTabsGroup = segments[0] === '(tabs)';

    // Public read-only routes a signed-out user can land on directly without
    // being bounced back to the map. /post/[id] is public — observations are
    // browseable content and the map's pin tap navigates here. /post/create
    // stays gated because it's a write action.
    const segs0 = segments as unknown as string[];
    const isPublicPostView = segs0[0] === 'post' && segs0[1] && segs0[1] !== 'create';
    // /structure/[id] and /structure/threads are public so a shared property
    // link works for recipients without an account (e.g. an insurer sent a
    // link to a home's observations) — they read only world-readable data
    // (structures + posts). /structure/checklist/[id] is included too: it's
    // just a redirect shim to /structure/[id] now (the checklist itself
    // moved onto that public page, gated internally per-section), and it
    // needs to run for signed-out visitors as much as anyone so an old
    // checklist link still lands somewhere real instead of bouncing to tabs.
    const isPublicStructureView = segs0[0] === 'structure' && Boolean(segs0[1]);
    // /plan/[id] is a shared defense plan: world-readable when shared, so a
    // recipient without an account lands on it (web redirects into the map,
    // native shows the open-on-web screen) instead of bouncing to tabs.
    const isPublicPlanView = segs0[0] === 'plan' && Boolean(segs0[1]);
    // /update is a signed-token link sent by email to someone reporting on
    // their assigned defense-plan tasks (a fire safe council, a county
    // office). They have no REDI account and never will — the token in the
    // URL is their whole authority, not a session — so this route must stay
    // reachable while signed out rather than bouncing to tabs like every
    // other unauthenticated destination.
    const isPublicUpdateView = segs0[0] === 'update';
    // /event/<id> is a community work day's page (Oct 2026): the time, the
    // place and a sign-up with a name and an email. It is shared by link to
    // neighbors who have no account, so it must not bounce to the map.
    const isPublicEventView = segs0[0] === 'event' && Boolean(segs0[1]);
    // /opportunity/<planId>/<bundleId> is a bundle of defense work open for
    // vendor bids (Oct 2026). A contractor with no account opens it from a
    // link and says "I can help", so it must not bounce to the map either.
    const isPublicOpportunityView = segs0[0] === 'opportunity' && Boolean(segs0[1]);
    // /start/<audience> is an audience intro page (scouts, fire officials,
    // community leaders, property owners). The whole point is that it is the
    // first thing somebody sees after a link in an email, before they have an
    // account, so it must not bounce to the map.
    const isPublicAudienceIntro = segs0[0] === 'start' && Boolean(segs0[1]);
    // FAQ / terms / privacy are informational — they back the "learn before you
    // sign up" goal and the legal-guidance copy, so signed-out visitors can read
    // them directly (e.g. from a link on the marketing site).
    const isPublicProfileInfo =
      segs0[0] === 'profile' && ['faq', 'terms', 'privacy'].includes(segs0[1]);
    // Let unmatched URLs render +not-found instead of silently landing on the
    // map — a mistyped share link should say so.
    const isNotFound = segs0[0] === '+not-found';
    // /dev/* is the hidden design-system gallery (app/dev/design-system.tsx)
    // — it gates its own content behind __DEV__ or the audit-fixtures
    // localStorage flag and renders +not-found otherwise, so this route
    // must stay reachable signed out too, or a signed-out capture/audit run
    // would get bounced to the map before that gate ever runs.
    const isDevRoute = segs0[0] === 'dev';

    if (
      !user &&
      !inAuthGroup &&
      !inTabsGroup &&
      !isPublicPostView &&
      !isPublicStructureView &&
      !isPublicPlanView &&
      !isPublicUpdateView &&
      !isPublicEventView &&
      !isPublicOpportunityView &&
      !isPublicAudienceIntro &&
      !isPublicProfileInfo &&
      !isNotFound &&
      !isDevRoute
    ) {
      // Unauthenticated users outside tabs → send to tabs (map is index)
      router.replace('/(tabs)');
    } else if (user && inAuthGroup) {
      // Every authed user — anon Fire Scout or full email signup — lands on
      // the map. Role selection, location, and property claim are no longer
      // forced; they're surfaced in-context (via Tour REDI, the structure
      // detail sheet, and the map's locate FAB).
      router.replace('/(tabs)');
    }
  }, [user, segments, isInitialized, router]);
}

function GlobalAddPropertySheet() {
  const open = useUIStore((s) => s.activeSheet === 'addProperty');
  const initialQuery = useUIStore((s) => s.addPropertyInitialQuery);
  const close = useUIStore((s) => s.closeSheet);
  return <AddPropertySheet visible={open} onClose={close} initialAddress={initialQuery} />;
}

/** True when the desktop map route docks the add-property/property-picker
 *  flow itself (PropertyDockPanel.web.tsx, app/(tabs)/index.tsx) instead of
 *  the two global AdaptiveSheet dialogs below — panel-system.md Phase 2.
 *  Everywhere else (profile, the structure page, phone) keeps the sheets. */
function useSuppressGlobalPropertySheets(): boolean {
  const isDesktopShell = useDesktopShell();
  const pathname = usePathname();
  return isDesktopShell && isMapRoute(pathname);
}

// Moderator-only import flow (canvas, a file input, pdfjs-dist — no native
// equivalent). It and its dependencies are heavy and rarely opened, so the
// component itself is code-split out of the entry bundle: this host waits
// for the first request to open it before importing the chunk, then keeps
// it mounted so later opens/closes still get AdaptiveSheet's animation.
const LazyPlanMapImportSheet = lazy(() =>
  import('../src/components/map/PlanMapImportSheet.web').then((m) => ({
    default: m.PlanMapImportSheet,
  }))
);

function PlanMapImportSheetHost() {
  const requested = useUIStore((s) => s.activeSheet === 'planMapImport');
  const [everRequested, setEverRequested] = useState(false);
  useEffect(() => {
    if (requested) setEverRequested(true);
  }, [requested]);
  if (!everRequested) return null;
  return (
    <Suspense fallback={null}>
      <LazyPlanMapImportSheet />
    </Suspense>
  );
}

function RootLayout() {
  const { user, isInitialized, initialize: initializeAuth } = useAuthStore();
  const segments = useSegments();
  const { initialize: initializeTheme } = useThemeStore();
  const initializeServiceAreas = useServiceAreasStore((s) => s.initialize);
  const { colors } = useTheme();
  const [mounted, setMounted] = useState(false);
  useSyncWebThemeVars();
  useBrandResolver();
  const isDesktopShell = useDesktopShell();
  const suppressGlobalPropertySheets = useSuppressGlobalPropertySheets();

  // Load custom fonts
  const [fontsLoaded] = useFonts({
    Inter_300Light,
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
  });

  useEffect(() => {
    const unsubscribeAuth = initializeAuth();
    const unsubscribeTheme = initializeTheme();
    // Fire-and-forget; the store falls back to bootstrap data while the
    // fetch is in flight, so we don't need to await this for first paint.
    void initializeServiceAreas();
    setMounted(true);

    return () => {
      if (typeof unsubscribeAuth === 'function') {
        unsubscribeAuth();
      }
      if (typeof unsubscribeTheme === 'function') {
        unsubscribeTheme();
      }
    };
  }, [initializeAuth, initializeTheme, initializeServiceAreas]);

  // Register the Workbox service worker (web, production only) so offline
  // precache + runtime caching actually run, and surface a reload prompt when
  // an update is waiting. No-op on native.
  useEffect(() => {
    registerServiceWorker({ onUpdate: () => useUIStore.getState().flagUpdateAvailable() });
  }, []);

  // Durable offline outbox for observations: hydrate the queue and wire its
  // drain triggers (online, foreground, sign-in). No-op wherever the outbox
  // itself is (native, OUTBOX_ENABLED off — see src/constants/outbox.ts).
  useOutboxSync();

  // Log the real build identifiers once at boot (git SHA + build time baked
  // in by app.config.ts) so a build running in the wild can be confirmed
  // remotely via console, without depending on the profile screen.
  useEffect(() => {
    const extra = Constants.expoConfig?.extra as
      | { buildSha?: string; builtAt?: string }
      | undefined;
    console.log(`[redi] build ${extra?.buildSha ?? 'dev'} ${extra?.builtAt ?? 'unknown'}`);
  }, []);

  useProtectedRoute(user, isInitialized && mounted);

  // Subscribe to unread notification count while user is authenticated
  useEffect(() => {
    if (user) {
      const unsub = useNotificationsStore.getState().subscribeToUnreadCount(user.id);
      return unsub;
    }
  }, [user]);

  // On web, blur the currently-focused element whenever the route segment
  // changes so react-navigation's aria-hidden on the outgoing screen doesn't
  // retain focus (Chrome logs an a11y violation otherwise).
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const active = document.activeElement as HTMLElement | null;
    if (active && typeof active.blur === 'function') active.blur();
  }, [segments]);

  // Fade out and remove the HTML splash (app/+html.tsx's #redi-splash) once
  // fonts + auth are ready. Web only — native never rendered that node, so it
  // keeps the LoadingSpinner below. If the element is missing (e.g. it was
  // already removed, or SSR output changed) this is a no-op rather than a
  // throw.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!isInitialized || !fontsLoaded) return;
    const splash = document.getElementById('redi-splash');
    if (!splash) return;
    splash.classList.add('redi-splash--out');
    const removalMs = durations.slow + 60; // fade duration + margin
    const timer = setTimeout(() => splash.remove(), removalMs);
    return () => clearTimeout(timer);
  }, [isInitialized, fontsLoaded]);

  // Show loading while fonts or auth are initializing. On web the HTML
  // splash (#redi-splash, painted before any JS ran) already sits on top of
  // this, so a plain background avoids a second, different-looking loader
  // showing through or flashing underneath it.
  if (!isInitialized || !fontsLoaded) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: colors.background }]}>
        {Platform.OS !== 'web' && <LoadingSpinner size="large" />}
      </View>
    );
  }

  const inAuthOrOnboarding = segments[0] === '(auth)' || segments[0] === '(onboarding)';
  // Desktop web shell: persistent sidebar replaces the floating pill bar.
  // Mobile/tablet/native behavior is untouched (useDesktopShell is web-only).
  // Focused content routes hide the floating nav so it doesn't compete with
  // the screen's primary action. Driven here (rather than per-screen via
  // useEffect) so the bar doesn't flash on mount before the screen runs.
  // /profile and its sub-routes (account, faq, notifications, etc.) are all
  // accessed via the avatar dropdown — none of them are tab destinations,
  // so they all hide the bar.
  // Cast through string[] so the tuple narrowing from expo-router's typegen
  // (which lives in .expo/types/router.d.ts and isn't generated in CI) doesn't
  // make seg1 unreachable. Runtime is always a plain array of segment strings.
  const segs = segments as unknown as string[];
  const seg0 = segs[0];
  // /structure/checklist/[id] used to be a focused drill-in screen and hid
  // the bar; it's now a redirect shim straight back to /structure/[id]
  // (which is not focused, and shows the bar), so there's nothing left to
  // carve out here.
  const isFocusedRoute =
    seg0 === 'post' || // /post/create and /post/[id]
    seg0 === 'profile' || // /profile and all sub-routes
    seg0 === 'update' || // /update — a token link for someone with no account
    seg0 === 'event' || // /event/<id> — a shared link to a work day's sign-up
    seg0 === 'opportunity' || // /opportunity/<planId>/<bundleId> — a vendor's bid page
    seg0 === 'start'; // /start/<audience> — an intro page reached from a link
  // Tab bar shows everywhere except focused content routes and the legacy
  // (auth)/(onboarding) groups. Onboarding is no longer forced, so every
  // authed user (anon or full) gets the bar by default.
  const showTabBar = !inAuthOrOnboarding && !isFocusedRoute;
  // /update never shows the desktop sidebar either — nav destinations like
  // "My properties" or sign-in are meaningless to a visitor with no account,
  // and a sidebar next to their one task would just be noise. Same for
  // /start/<audience>: it is somebody's first second in REDI, arriving from
  // an email, and its whole job is one button.
  // /event/<id> likewise: a neighbor opening a sign-up link wants the time,
  // the place and one form, not the app's navigation.
  const isChromeless =
    inAuthOrOnboarding ||
    seg0 === 'update' ||
    seg0 === 'start' ||
    seg0 === 'event' ||
    seg0 === 'opportunity';

  return (
    <GestureHandlerRootView style={styles.root}>
      <ErrorBoundary>
        <BottomSheetModalProvider>
          <ConfirmProvider>
            <AppShell chromeless={isChromeless} mapTools={<MapRailTools />}>
              <Stack
                screenOptions={{
                  headerShown: false,
                  contentStyle: { backgroundColor: colors.background },
                }}>
                <Stack.Screen name="(auth)" />
                <Stack.Screen name="(onboarding)" />
                <Stack.Screen name="(tabs)" />
              </Stack>
            </AppShell>
            <AuthGateBottomSheet />
            {/* print-hide: the readiness record prints from this route tree,
                and the floating bar would otherwise land in the PDF. */}
            {!isDesktopShell && showTabBar && (
              <View {...({ dataSet: { printHide: 'true' } } as object)}>
                <GlobalFloatingTabBar />
              </View>
            )}
            {/* Always mounted: focused routes (/post/*, /profile/*) hide the tab
                bar but their headers still show the avatar trigger — gating this
                on showTabBar made those taps no-ops. */}
            <UserMenuDropdown />
            <CreateActionSheet />
            <HowReDIWorksSheet />
            <ObservationGuidanceSheet />
            <MapLayersSheet />
            {/* Canvas/file-input/pdfjs-dist import flow — no native
                equivalent, so this one mount is web only. */}
            {Platform.OS === 'web' && <PlanMapImportSheetHost />}
            {/* The desktop map route docks these two instead (Property
                DockPanel.web.tsx, panel-system.md Phase 2) — suppressed here
                so the flow never presents as both a dialog and a dock at
                once. */}
            {!suppressGlobalPropertySheets && <GlobalAddPropertySheet />}
            {!suppressGlobalPropertySheets && <PropertyPickerSheet />}
            <ToastHost />
            <CommandPaletteHost />
          </ConfirmProvider>
        </BottomSheetModalProvider>
      </ErrorBoundary>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default Sentry.wrap(RootLayout);
